import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { TrailerInterventionService } from '../../stores/trailer-intervention.service';
import { TrailerService } from '../../stores/trailer.service';
import { TractorService } from '../../stores/tractor.service';
import { FieldService } from '../../stores/field.service';
import { CropService } from '../../stores/crop.service';
import { UserDomainService } from '../../user-domains/user-domain.service';
import { notify, ConfigService, User } from 'shared';
import * as L from 'leaflet';
import { Map, Polygon, tileLayer, FeatureGroup, latLng } from 'leaflet';
import 'leaflet-draw';

interface ManualInterventionForm {
  trailer_id: number | null;
  vehicle_id: number | null;
  field_id: number | null;
  crop_id: number | null; // Culture/Crop selection
  start_time: Date | null;
  stop_time: Date | null;
  working_area: number | null; // in square meters, will be calculated from geometry
  working_multipolygon: any; // GeoJSON geometry
  // Optional fields
  working_in_field_move_duration?: number;
  working_in_field_idle_duration?: number;
  working_in_field_off_duration?: number;
  working_out_field_move_duration?: number;
  working_out_field_idle_duration?: number;
  working_out_field_off_duration?: number;
  road_move_duration?: number;
  road_idle_duration?: number;
  road_off_duration?: number;
  working_in_field_speed_avg?: number;
  working_in_field_speed_max?: number;
  pressure_avg?: number;
  pressure_max?: number;
  total_fuel_consumption?: number;
  fuel_consumption_per_hectare?: number;
}

@Component({
  selector: 'app-manual-intervention-dialog',
  templateUrl: './manual-intervention-dialog.component.html',
  styleUrls: ['./manual-intervention-dialog.component.scss']
})
export class ManualInterventionDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  @Output() onCreated = new EventEmitter<void>();
  
  visible = false;
  form: ManualInterventionForm = {
    trailer_id: null,
    vehicle_id: null,
    field_id: null,
    crop_id: null,
    start_time: null,
    stop_time: null,
    working_area: null,
    working_multipolygon: null
  };
  
  // Data sources
  trailers: any[] = [];
  vehicles: any[] = [];
  fields: any[] = [];
  crops: any[] = [];
  
  // Map
  map: Map;
  editableLayers: FeatureGroup;
  drawControl: L.Control.Draw;
  layersControl: L.Control.Layers;
  
  // Map layers
  satelliteLayer = tileLayer('https://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}&hl=ar-MA&gl=MA', {
    maxZoom: 21,
    subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
  });
  
  hybridLayer = tileLayer('https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}&hl=ar-MA&gl=MA', {
    maxZoom: 21,
    subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
  });
  
  // Map options - default to satellite
  mapOptions = {
    layers: [this.satelliteLayer],
    zoom: 13,
    center: [33.626756199999996, -7.4964748] as [number, number]
  };
  
  isSubmitting = false;

  // Editor options for field select box
  get fieldSelectBoxOptions() {
    return {
      dataSource: this.fields,
      displayExpr: 'name',
      valueExpr: 'id',
      searchEnabled: true,
      searchMode: 'contains',
      searchExpr: ['name', 'code'],
      placeholder: 'Sélectionner une parcelle',
      onValueChanged: (e: any) => this.onFieldChange(e.value)
    };
  }

  // Editor options for stop time date box
  get stopTimeEditorOptions() {
    return {
      type: 'datetime',
      useMaskBehavior: true,
      showClearButton: true,
      min: this.form.start_time
    };
  }

  constructor(
    private trailerInterventionService: TrailerInterventionService,
    private trailerService: TrailerService,
    private tractorService: TractorService,
    private fieldService: FieldService,
    private cropService: CropService,
    private userDomainService: UserDomainService,
    private config: ConfigService,
    public user: User,
    private cdr: ChangeDetectorRef
  ) {
    this.editableLayers = new FeatureGroup();
  }

  ngOnInit(): void {
    this.loadDataSources();
  }

  ngAfterViewInit(): void {
    // Map will be initialized when dialog opens
  }

  onMapReady(map: Map): void {
    this.map = map;
    
    // Add layer control for switching between map types
    this.layersControl = L.control.layers({
      'Satellite': this.satelliteLayer,
      'Hybride': this.hybridLayer
    }, {}, {
      position: 'topright'
    }).addTo(map);
    
    this.initializeMapDrawing();
  }

  ngOnDestroy(): void {
    if (this.drawControl) {
      this.map?.removeControl(this.drawControl);
    }
    if (this.layersControl) {
      this.map?.removeControl(this.layersControl);
    }
    if (this.editableLayers) {
      this.map?.removeLayer(this.editableLayers);
    }
  }

  loadDataSources(): void {
    // Load trailers (backend already filters by domain via X-DOMAIN header)
    // DataSource.load() returns the data array directly when paginate: false
    const trailerDs = this.trailerService.newDataSource({ paginate: false });
    trailerDs.load().then((result: any) => {
      console.log('Trailer load result:', result, 'Type:', typeof result, 'IsArray:', Array.isArray(result));
      // DataSource.load() with paginate: false should return array directly
      // But if it returns ModelsList, extract data property
      const trailers = Array.isArray(result) ? result : (result?.data || []);
      this.trailers = trailers;
      console.log('Loaded trailers:', this.trailers.length);
      if (this.trailers.length > 0) {
        console.log('Sample trailer:', this.trailers[0]);
      } else {
        console.warn('No trailers loaded. Check backend response and domain filtering.');
      }
      this.cdr.detectChanges();
    }).catch((error) => {
      console.error('Error loading trailers:', error);
      this.trailers = [];
      this.cdr.detectChanges();
    });

    // Load vehicles (tractors) - backend already filters by domain
    const vehicleDs = this.tractorService.newDataSource({ paginate: false });
    vehicleDs.load().then((result: any) => {
      console.log('Vehicle load result:', result, 'Type:', typeof result, 'IsArray:', Array.isArray(result));
      const vehicles = Array.isArray(result) ? result : (result?.data || []);
      this.vehicles = vehicles;
      console.log('Loaded vehicles:', this.vehicles.length);
      if (this.vehicles.length > 0) {
        console.log('Sample vehicle:', this.vehicles[0]);
      } else {
        console.warn('No vehicles loaded. Check backend response and domain filtering.');
      }
      this.cdr.detectChanges();
    }).catch((error) => {
      console.error('Error loading vehicles:', error);
      this.vehicles = [];
      this.cdr.detectChanges();
    });

    // Load fields - backend already filters by domain
    const fieldDs = this.fieldService.newDataSource({ paginate: false });
    fieldDs.load().then((result: any) => {
      console.log('Field load result:', result, 'Type:', typeof result, 'IsArray:', Array.isArray(result));
      const fields = Array.isArray(result) ? result : (result?.data || []);
      this.fields = fields;
      console.log('Loaded fields:', this.fields.length);
      if (this.fields.length > 0) {
        console.log('Sample field:', this.fields[0]);
      } else {
        console.warn('No fields loaded. Check backend response and domain filtering.');
      }
      this.cdr.detectChanges();
    }).catch((error) => {
      console.error('Error loading fields:', error);
      this.fields = [];
      this.cdr.detectChanges();
    });

    // Load crops - backend already filters by domain
    this.cropService.newDataSource({ paginate: false }).load().then((result: any) => {
      const crops = Array.isArray(result) ? result : (result?.data || []);
      this.crops = crops;
      console.log('Loaded crops:', this.crops.length);
      if (this.crops.length > 0) {
        console.log('Sample crop:', this.crops[0]);
      }
      this.cdr.detectChanges();
    }).catch((error) => {
      console.error('Error loading crops:', error);
      this.crops = [];
      this.cdr.detectChanges();
    });
  }

  initializeMapDrawing(): void {
    if (!this.map) return;

    this.map.addLayer(this.editableLayers);

    // Initialize draw control for polygons
    this.drawControl = new L.Control.Draw({
      draw: {
        polygon: {
          allowIntersection: true, // Allow self-intersection to prevent premature closing
          showArea: true,
          // No maxPoints limit - user can add unlimited points until they finish
          // Minimum is 3 points (required for polygon), but no maximum
          // Polygon will only close when user double-clicks or clicks the first point
        },
        polyline: false,
        circle: false,
        rectangle: false,
        marker: false,
        circlemarker: false
      },
      edit: {
        featureGroup: this.editableLayers,
        remove: false
      }
    } as any);

    this.map.addControl(this.drawControl);

    // Handle draw events
    this.map.on(L.Draw.Event.CREATED, (e: L.DrawEvents.Created) => {
      const layer = e.layer as Polygon;
      this.editableLayers.clearLayers();
      this.editableLayers.addLayer(layer);
      
      // Get GeoJSON and calculate area
      const geoJson = layer.toGeoJSON();
      this.form.working_multipolygon = geoJson.geometry;
      
      // Calculate area in square meters
      const area = this.calculatePolygonArea(geoJson.geometry);
      this.form.working_area = area;
      
      this.cdr.detectChanges();
    });

    this.map.on(L.Draw.Event.EDITED, (e: L.DrawEvents.Edited) => {
      const layers = e.layers;
      layers.eachLayer((layer: any) => {
        const geoJson = layer.toGeoJSON();
        this.form.working_multipolygon = geoJson.geometry;
        const area = this.calculatePolygonArea(geoJson.geometry);
        this.form.working_area = area;
      });
      this.cdr.detectChanges();
    });

    this.map.on(L.Draw.Event.DELETED, () => {
      this.form.working_multipolygon = null;
      this.form.working_area = null;
      this.cdr.detectChanges();
    });
  }

  // Calculate polygon area in square meters using spherical excess formula
  calculatePolygonArea(geometry: any): number {
    if (!geometry || geometry.type !== 'Polygon' || !geometry.coordinates || geometry.coordinates.length === 0) {
      return 0;
    }

    const coordinates = geometry.coordinates[0]; // First ring (exterior)
    if (coordinates.length < 3) return 0;

    let area = 0;
    for (let i = 0; i < coordinates.length - 1; i++) {
      const p1 = coordinates[i];
      const p2 = coordinates[i + 1];
      area += (p1[0] * p2[1] - p2[0] * p1[1]) * 111320 * 111320 * Math.cos((p1[1] + p2[1]) / 2 * Math.PI / 180);
    }
    return Math.abs(area) / 2;
  }

  show(): void {
    this.visible = true;
    this.resetForm();
    // Center map on selected field if available
    if (this.form.field_id) {
      const field = this.fields.find(f => f.id === this.form.field_id);
      if (field && field.polygon) {
        // Center on field
        setTimeout(() => {
          if (this.map && field.polygon) {
            try {
              const fieldLayer = (field as any).createLayer();
              if (fieldLayer && fieldLayer.getBounds) {
                this.map.fitBounds(fieldLayer.getBounds());
              }
            } catch (error) {
              console.error('Error centering map on field:', error);
            }
          }
        }, 100);
      }
    }
  }

  close(): void {
    this.visible = false;
    this.resetForm();
    this.editableLayers.clearLayers();
  }

  resetForm(): void {
    this.form = {
      trailer_id: null,
      vehicle_id: null,
      field_id: null,
      crop_id: null,
      start_time: new Date(),
      stop_time: new Date(),
      working_area: null,
      working_multipolygon: null
    };
  }

  onFieldChange(fieldId: number): void {
    if (fieldId && this.map) {
      const field = this.fields.find(f => f.id === fieldId);
      if (field && field.polygon) {
        try {
          // Field extends BasePolygonModel which has createLayer() method
          // that converts WKT polygon to Leaflet layer
          const fieldLayer = (field as any).createLayer();
          if (fieldLayer && fieldLayer.getBounds) {
            this.map.fitBounds(fieldLayer.getBounds());
          }
        } catch (error) {
          console.error('Error centering map on field:', error);
          // Fallback: try using geojson property if available
          try {
            const geojson = (field as any).geojson;
            if (geojson) {
              this.map.fitBounds(L.geoJSON(geojson as any).getBounds());
            }
          } catch (e) {
            console.error('Error using geojson fallback:', e);
          }
        }
      }
    }
  }

  get workingAreaHa(): number {
    return this.form.working_area ? this.form.working_area / 10000 : 0;
  }

  isFormValid(): boolean {
    return !!(
      this.form.trailer_id &&
      this.form.vehicle_id &&
      this.form.field_id &&
      this.form.start_time &&
      this.form.stop_time &&
      this.form.working_multipolygon &&
      this.form.working_area &&
      this.form.working_area > 0
    );
  }

  submit(): void {
    if (!this.isFormValid()) {
      notify('Veuillez remplir tous les champs obligatoires et dessiner la zone de travail sur la carte', 'error', 3000);
      return;
    }

    if (!this.form.working_multipolygon) {
      notify('Veuillez dessiner la zone de travail sur la carte', 'error', 3000);
      return;
    }

    this.isSubmitting = true;

    // Prepare request payload
    const payload: any = {
      trailer_id: this.form.trailer_id,
      vehicle_id: this.form.vehicle_id,
      field_id: this.form.field_id,
      start_time: this.form.start_time instanceof Date 
        ? this.form.start_time.toISOString() 
        : this.form.start_time,
      stop_time: this.form.stop_time instanceof Date 
        ? this.form.stop_time.toISOString() 
        : this.form.stop_time,
      working_area: this.form.working_area,
      working_multipolygon: this.form.working_multipolygon,
      is_manual: true
    };

    // Add crop_id (culture_id) if selected
    if (this.form.crop_id) {
      payload.culture_id = this.form.crop_id;
    }

    // Add optional fields only if they have meaningful values (not 0, null, or undefined)
    if (this.form.working_in_field_move_duration && this.form.working_in_field_move_duration > 0) {
      payload.working_in_field_move_duration = this.form.working_in_field_move_duration;
    }
    if (this.form.working_in_field_idle_duration && this.form.working_in_field_idle_duration > 0) {
      payload.working_in_field_idle_duration = this.form.working_in_field_idle_duration;
    }
    if (this.form.working_in_field_off_duration && this.form.working_in_field_off_duration > 0) {
      payload.working_in_field_off_duration = this.form.working_in_field_off_duration;
    }
    if (this.form.working_out_field_move_duration && this.form.working_out_field_move_duration > 0) {
      payload.working_out_field_move_duration = this.form.working_out_field_move_duration;
    }
    if (this.form.working_out_field_idle_duration && this.form.working_out_field_idle_duration > 0) {
      payload.working_out_field_idle_duration = this.form.working_out_field_idle_duration;
    }
    if (this.form.working_out_field_off_duration && this.form.working_out_field_off_duration > 0) {
      payload.working_out_field_off_duration = this.form.working_out_field_off_duration;
    }
    if (this.form.road_move_duration && this.form.road_move_duration > 0) {
      payload.road_move_duration = this.form.road_move_duration;
    }
    if (this.form.road_idle_duration && this.form.road_idle_duration > 0) {
      payload.road_idle_duration = this.form.road_idle_duration;
    }
    if (this.form.road_off_duration && this.form.road_off_duration > 0) {
      payload.road_off_duration = this.form.road_off_duration;
    }
    if (this.form.working_in_field_speed_avg !== undefined && this.form.working_in_field_speed_avg !== null && this.form.working_in_field_speed_avg > 0) {
      payload.working_in_field_speed_avg = this.form.working_in_field_speed_avg;
    }
    if (this.form.working_in_field_speed_max !== undefined && this.form.working_in_field_speed_max !== null && this.form.working_in_field_speed_max > 0) {
      payload.working_in_field_speed_max = this.form.working_in_field_speed_max;
    }
    if (this.form.pressure_avg !== undefined && this.form.pressure_avg !== null && this.form.pressure_avg > 0) {
      payload.pressure_avg = this.form.pressure_avg;
    }
    if (this.form.pressure_max !== undefined && this.form.pressure_max !== null && this.form.pressure_max > 0) {
      payload.pressure_max = this.form.pressure_max;
    }
    if (this.form.total_fuel_consumption !== undefined && this.form.total_fuel_consumption !== null && this.form.total_fuel_consumption > 0) {
      payload.total_fuel_consumption = this.form.total_fuel_consumption;
    }
    if (this.form.fuel_consumption_per_hectare !== undefined && this.form.fuel_consumption_per_hectare !== null && this.form.fuel_consumption_per_hectare > 0) {
      payload.fuel_consumption_per_hectare = this.form.fuel_consumption_per_hectare;
    }

    // Call backend API
    this.trailerInterventionService.createManual(payload).subscribe({
      next: () => {
        notify('Intervention créée avec succès', 'success', 3000);
        this.onCreated.emit();
        this.close();
        this.isSubmitting = false;
      },
      error: (err) => {
        console.error('Error creating manual intervention:', err);
        const errorMessage = err?.error?.error || err?.message || 'Erreur lors de la création de l\'intervention';
        notify(errorMessage, 'error', 5000);
        this.isSubmitting = false;
        // Trigger change detection to re-enable the form
        this.cdr.detectChanges();
      }
    });
  }
}

